import { useCallback, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const useNaverDiscoverData = ({ eventTypeFilter, filterState, userLocation, distanceKm }) => {
  const [events, setEvents] = useState([]);
  const [places, setPlaces] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesError, setPlacesError] = useState('');
  const lastEventsAbortRetryAtRef = useRef(0);
  const lastPlacesAbortRetryAtRef = useRef(0);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    setEventsError('');
    try {
      const { data, error } = await supabase
        .from('session_invites')
        .select(
          'id, token, title, sport, date, end_date, start_time, capacity, registered, status, venue, venue_address, venue_lat, venue_lng, cover_image_url, cover_image_thumb_url, image_urls, created_at, event_type, price, bank_name, account_number, account_holder, payment_deadline'
        )
        .order('created_at', { ascending: false });

      if (error) {
        if (error?.name === 'AbortError' || String(error?.message || '').includes('AbortError')) {
          return [];
        }
        setEventsError(error?.message || '이벤트 정보를 불러오지 못했습니다.');
        setEvents([]);
        return [];
      }

      const mapped = (data || []).map((row) => {
        const lat = row?.venue_lat != null ? Number(row.venue_lat) : null;
        const lng = row?.venue_lng != null ? Number(row.venue_lng) : null;
        return {
          id: String(row?.id || ''),
          token: String(row?.token || ''),
          title: String(row?.title || ''),
          sportType: String(row?.sport || ''),
          date: String(row?.date || ''),
          endDate: String(row?.end_date || ''),
          time: String(row?.start_time || ''),
          maxParticipants: row?.capacity != null ? Number(row.capacity) : null,
          participantCount: row?.registered != null ? Number(row.registered) : null,
          status: String(row?.status || ''),
          location: String(row?.venue || ''),
          venueAddress: String(row?.venue_address || ''),
          latitude: Number.isFinite(lat) ? lat : null,
          longitude: Number.isFinite(lng) ? lng : null,
          coverImageUrl: String(row?.cover_image_url || ''),
          coverImageThumbUrl: String(row?.cover_image_thumb_url || ''),
          imageUrls: Array.isArray(row?.image_urls) ? row.image_urls : [],
          price: row?.price != null ? Number(row.price) : null,
          bankName: String(row?.bank_name || ''),
          accountNumber: String(row?.account_number || ''),
          accountHolder: String(row?.account_holder || ''),
          paymentDeadline: String(row?.payment_deadline || ''),
          createdAt: String(row?.created_at || ''),
          eventType: String(row?.event_type || 'open_game'),
        };
      });

      const filtered = mapped.filter((event) => typeof event.latitude === 'number' && typeof event.longitude === 'number');
      setEvents(filtered);
      return filtered;
    } catch (err) {
      if (err?.name === 'AbortError') {
        const now = Date.now();
        const last = lastEventsAbortRetryAtRef.current || 0;
        if (now - last > 1500) {
          lastEventsAbortRetryAtRef.current = now;
          window.setTimeout(() => {
            fetchEvents();
          }, 300);
        }
        return [];
      }
      setEventsError('이벤트 정보를 불러오는 중 오류가 발생했습니다.');
      setEvents([]);
      return [];
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const fetchPlaces = useCallback(async () => {
    setPlacesLoading(true);
    setPlacesError('');
    try {
      const { data, error } = await supabase
        .from('pickleball_codes')
        .select(
          'id, name, cover_image_url, cover_image_thumb_url, gallery_urls, indoor, address, venue_lat, venue_lng, court_count, floor_material, amenities, equipment_rental, supplies, website_url, business_hours, price_info'
        )
        .order('created_at', { ascending: false });

      if (error) {
        if (error?.name === 'AbortError' || String(error?.message || '').includes('AbortError')) {
          return [];
        }
        setPlacesError(error?.message || '장소 정보를 불러오지 못했습니다.');
        setPlaces([]);
        return [];
      }

      const mapped = (data || [])
        .map((row) => {
          const lat = row?.venue_lat != null ? Number(row.venue_lat) : null;
          const lng = row?.venue_lng != null ? Number(row?.venue_lng) : null;
          const galleryUrls = Array.isArray(row?.gallery_urls) ? row.gallery_urls : [];
          const coverImageUrl = String(row?.cover_image_url || '').trim();
          const coverImageThumbUrl = String(row?.cover_image_thumb_url || '').trim();
          return {
            id: String(row?.id || ''),
            name: String(row?.name || ''),
            coverImageUrl,
            coverImageThumbUrl,
            galleryUrls,
            indoor: Boolean(row?.indoor),
            address: String(row?.address || ''),
            latitude: Number.isFinite(lat) ? lat : null,
            longitude: Number.isFinite(lng) ? lng : null,
            courtCount: row?.court_count != null ? Number(row.court_count) : null,
            floorMaterial: String(row?.floor_material || ''),
            websiteUrl: String(row?.website_url || ''),
            businessHours: String(row?.business_hours || ''),
            priceInfo: Array.isArray(row?.price_info) ? row.price_info : [],
            amenities: row?.amenities || {},
            equipmentRental: Boolean(row?.equipment_rental),
            supplies: String(row?.supplies || ''),
          };
        })
        .filter((place) => typeof place.latitude === 'number' && typeof place.longitude === 'number');

      setPlaces(mapped);
      return mapped;
    } catch (err) {
      if (err?.name === 'AbortError') {
        const now = Date.now();
        const last = lastPlacesAbortRetryAtRef.current || 0;
        if (now - last > 1500) {
          lastPlacesAbortRetryAtRef.current = now;
          window.setTimeout(() => {
            fetchPlaces();
          }, 300);
        }
        return [];
      }
      setPlacesError('장소 정보를 불러오는 중 오류가 발생했습니다.');
      setPlaces([]);
      return [];
    } finally {
      setPlacesLoading(false);
    }
  }, []);

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (!eventTypeFilter.includes(event.eventType)) return false;
      if (filterState.availability === 'available' && event.participantCount >= event.maxParticipants) return false;
      if (filterState.availability === 'full' && event.participantCount < event.maxParticipants) return false;
      return true;
    });
  }, [events, eventTypeFilter, filterState.availability]);

  const nearbyEvents = useMemo(() => filteredEvents, [filteredEvents]);

  const placeEventMap = useMemo(() => {
    const map = new Map();
    const sameSpot = (a, b) => {
      if (!a || !b) return false;
      const latDiff = Math.abs(a.latitude - b.latitude);
      const lngDiff = Math.abs(a.longitude - b.longitude);
      return latDiff < 0.00015 && lngDiff < 0.00015;
    };
    places.forEach((place) => {
      const eventsAtPlace = nearbyEvents.filter((event) => sameSpot(place, event));
      map.set(place.id, eventsAtPlace);
    });
    return map;
  }, [nearbyEvents, places]);

  const placeList = useMemo(() => {
    return (places || []).map((place) => ({
      ...place,
      eventCount: (placeEventMap.get(place.id) || []).length,
    }));
  }, [placeEventMap, places]);

  const listEvents = useMemo(() => {
    const list = Array.isArray(nearbyEvents) ? nearbyEvents : [];
    if (!userLocation) return list;
    const center = { lat: userLocation.lat, lng: userLocation.lng };
    return list
      .map((event) => {
        const d = distanceKm(center, { lat: event.latitude, lng: event.longitude });
        const safeDistance = Number.isFinite(d) ? d : Number.POSITIVE_INFINITY;
        const distanceLabel = Number.isFinite(d) ? `${d.toFixed(d < 10 ? 1 : 0)}km` : '';
        return { ...event, distance: distanceLabel, __distance: safeDistance };
      })
      .sort((a, b) => (a.__distance ?? Number.POSITIVE_INFINITY) - (b.__distance ?? Number.POSITIVE_INFINITY));
  }, [distanceKm, nearbyEvents, userLocation]);

  const mapCenter = useMemo(() => {
    if (filteredEvents.length > 0) {
      return { lat: filteredEvents[0].latitude, lng: filteredEvents[0].longitude };
    }
    if (userLocation) {
      return { lat: userLocation.lat, lng: userLocation.lng };
    }
    return { lat: 37.5665, lng: 126.9780 };
  }, [filteredEvents, userLocation]);

  return {
    events,
    setEvents,
    eventsLoading,
    eventsError,
    places,
    placesLoading,
    placesError,
    fetchEvents,
    fetchPlaces,
    filteredEvents,
    nearbyEvents,
    placeEventMap,
    placeList,
    listEvents,
    mapCenter,
  };
};

export default useNaverDiscoverData;
